import * as THREE from 'three';

/**
 * Rim light patches into an EXISTING material via `onBeforeCompile`.
 * Sampled texture/color stays the base; rim mixes in only at grazing
 * angles, at the silhouette. Never touches `map`/`color` — those are
 * the base material's own uniforms.
 *
 * (The inverted-hull outline that used to live alongside this was
 * reverted 2026-08-18 — it broke boss/elite silhouettes three times
 * running and the pre-outline build already rendered correctly.)
 */

export type RimLightOptions = {
  rimColor: number;
  rimStrength?: number;
};

/**
 * Adds a camera-relative rim term to an existing material's fragment
 * shader, via `onBeforeCompile`. Works on any built-in material
 * (MeshStandardMaterial, MeshBasicMaterial, MeshLambertMaterial, ...)
 * because it only touches two universal injection points every one of
 * them shares: `#include <common>` (declarations, always first) and
 * `#include <dithering_fragment>` (always last, right before the
 * fragment shader's own closing brace). Skinning and instancing are
 * handled entirely by three.js's own compile step, upstream of this
 * function — `transformedNormal` and `mvPosition` are already correct
 * for a skinned or instanced vertex by the time `<project_vertex>` has
 * run, so this needs no skinning/instancing code of its own.
 */
/**
 * Returns the live `THREE.Color` object backing the shader's `rimColor`
 * uniform. Uniforms are read by REFERENCE every frame, not re-compiled --
 * a caller that keeps this reference and mutates it later (`.setHex(...)`)
 * changes the rendered rim colour with no shader recompile, no
 * `material.needsUpdate` needed a second time. This is what lets a rim
 * colour that was fixed at load time (e.g. RIM_FRIENDLY, tied to
 * whichever character's tint was default at construction) track a LATER
 * runtime change instead of staying stuck on the load-time value.
 */
export function addRimLight(material: THREE.Material, opts: RimLightOptions): THREE.Color {
  const rimStrength = THREE.MathUtils.clamp(opts.rimStrength ?? 0.6, 0, 1);
  const rimColor = new THREE.Color(opts.rimColor);
  const priorOnBeforeCompile = material.onBeforeCompile?.bind(material);
  material.onBeforeCompile = (shader, renderer) => {
    priorOnBeforeCompile?.(shader, renderer);
    shader.uniforms['rimColor'] = { value: rimColor };
    shader.uniforms['rimStrength'] = { value: rimStrength };

    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        '#include <common>\nvarying vec3 vRimNormal;\nvarying vec3 vRimViewPosition;',
      )
      .replace(
        '#include <project_vertex>',
        '#include <project_vertex>\nvRimNormal = normalize(transformedNormal);\nvRimViewPosition = -mvPosition.xyz;',
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        '#include <common>\nuniform vec3 rimColor;\nuniform float rimStrength;\n'
          + 'varying vec3 vRimNormal;\nvarying vec3 vRimViewPosition;',
      )
      .replace(
        '#include <dithering_fragment>',
        // Only normals within a narrow band of perpendicular to the view
        // ray receive rim light — this is what separates overlapping
        // bodies at horde density without touching the base shading.
        'vec3 rimN = normalize(vRimNormal);\n'
          + 'vec3 rimV = normalize(vRimViewPosition);\n'
          + 'float rimFacing = abs(dot(rimN, rimV));\n'
          + 'float rimTerm = 1.0 - smoothstep(0.012, 0.075, rimFacing);\n'
          + 'rimTerm = rimTerm * rimTerm * rimStrength;\n'
          + 'gl_FragColor.rgb = mix(gl_FragColor.rgb, rimColor, rimTerm);\n'
          + '#include <dithering_fragment>',
      );
  };
  material.needsUpdate = true;
  return rimColor;
}
